import {
  ORDER_STATUS_LABELS, PAYMENT_STATUS_LABELS, QUOTE_STATUS_LABELS, formatDate, formatILS, type OrderStatus, type PaymentStatus, type QuoteStatus,
} from '@takumi/shared';
import { useQuery } from '@tanstack/react-query';
import { FileText, ShoppingBag } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { PageHeader } from '@/components/AppShell';
import { Select } from '@/components/ui/form';
import { Badge, Card, EmptyState, Skeleton } from '@/components/ui/primitives';
import { api } from '@/lib/api';
import { ORDER_STATUS_VARIANT, PAYMENT_STATUS_VARIANT } from './OrderPage';
import { QUOTE_STATUS_VARIANT } from './QuotePage';

interface QuoteRow { id: string; number: string; status: QuoteStatus; issueDate: string; validUntil: string | null; total: number; organizationName: string; viewedAt: string | null }
interface OrderRow { id: string; number: string; status: OrderStatus; paymentStatus: PaymentStatus; orderDate: string; total: number; amountPaid: number; organizationName: string }

export function QuotesPage() {
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['quotes', status], queryFn: () => api.get<QuoteRow[]>('/quotes', { status }) });
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="הצעות מחיר" subtitle="הצעה חדשה נוצרת מתוך כרטיס הלקוח" actions={
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-40" aria-label="סטטוס">
          <option value="">כל הסטטוסים</option>
          {(Object.keys(QUOTE_STATUS_LABELS) as QuoteStatus[]).map((s) => <option key={s} value={s}>{QUOTE_STATUS_LABELS[s]}</option>)}
        </Select>
      } />
      <Card className="overflow-hidden">
        {isLoading && <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>}
        {data?.length === 0 && <EmptyState icon={<FileText />} title="אין הצעות מחיר" />}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-line/70">
            {data?.map((q) => (
              <tr key={q.id} className="cursor-pointer hover:bg-matcha-50/50" onClick={() => navigate(`/quotes/${q.id}`)}>
                <td className="ltr py-2.5 ps-4 text-start font-semibold">{q.number}</td>
                <td className="px-3 font-semibold">{q.organizationName}</td>
                <td className="px-3 text-muted">{formatDate(q.issueDate)}</td>
                <td className="px-3"><Badge variant={QUOTE_STATUS_VARIANT[q.status]}>{QUOTE_STATUS_LABELS[q.status]}</Badge></td>
                <td className="px-3 text-muted">{q.validUntil && `עד ${formatDate(q.validUntil)}`}</td>
                <td className="px-4 text-end font-semibold tabular">{formatILS(q.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export function OrdersPage() {
  const [status, setStatus] = useState('');
  const navigate = useNavigate();
  const { data, isLoading } = useQuery({ queryKey: ['orders', status], queryFn: () => api.get<OrderRow[]>('/orders', { status }) });
  return (
    <div className="mx-auto max-w-5xl">
      <PageHeader title="הזמנות" subtitle="הזמנה חדשה נוצרת מתוך כרטיס הלקוח או מהצעת מחיר" actions={
        <Select value={status} onChange={(e) => setStatus(e.target.value)} className="w-44" aria-label="סטטוס">
          <option value="">כל ההזמנות</option>
          <option value="unpaid">טרם שולמו</option>
          {(Object.keys(ORDER_STATUS_LABELS) as OrderStatus[]).map((s) => <option key={s} value={s}>{ORDER_STATUS_LABELS[s]}</option>)}
        </Select>
      } />
      <Card className="overflow-hidden">
        {isLoading && <div className="space-y-2 p-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-9" />)}</div>}
        {data?.length === 0 && <EmptyState icon={<ShoppingBag />} title="אין הזמנות" />}
        <table className="w-full text-sm">
          <tbody className="divide-y divide-line/70">
            {data?.map((o) => (
              <tr key={o.id} className="cursor-pointer hover:bg-matcha-50/50" onClick={() => navigate(`/orders/${o.id}`)}>
                <td className="ltr py-2.5 ps-4 text-start font-semibold">{o.number}</td>
                <td className="px-3 font-semibold">{o.organizationName}</td>
                <td className="px-3 text-muted">{formatDate(o.orderDate)}</td>
                <td className="px-3"><Badge variant={ORDER_STATUS_VARIANT[o.status]}>{ORDER_STATUS_LABELS[o.status]}</Badge></td>
                <td className="px-3"><Badge variant={PAYMENT_STATUS_VARIANT[o.paymentStatus]}>{PAYMENT_STATUS_LABELS[o.paymentStatus]}</Badge></td>
                <td className="px-4 text-end font-semibold tabular">{formatILS(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
